import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import useBrowseMode from "../../../hooks/useBrowseMode";
import { ROUTES } from "../../../common/navigation";
import {
  DEFAULT_DESCRIPTION,
  SITE_NAME,
  absoluteUrl,
  applyJsonLd,
  removeJsonLd,
} from "../../../common/seo";
import { TOTAL_CONCEPTS } from "../../../services/learning/conceptService";
import { BRANDED_TEAM_IDS } from "../../../data/teamBranding";
import { teamFullName } from "../../../data/teams";
import { IQ_LEVELS } from "../../../utils/footballIQ";
import { singleTeamWashStyle, teamWashStyle } from "../../../utils/teamWash";
import StatusBadge from "../../common/StatusBadge/StatusBadge";
import TeamLogo from "../../common/TeamLogo/TeamLogo";
import styles from "./Landing.module.css";

/* --------------------------------------------------------------- content */

const PAINS = [
  "You lose the thread by the second play.",
  "Asking a question out loud feels worse than not knowing.",
  "Every explainer online starts three levels above you.",
];

const STEPS = [
  {
    icon: "fa-solid fa-hand-pointer",
    title: "Pick a game",
    body: "Kickoff times, nothing to research. Open the game and you're in your seat.",
  },
  {
    icon: "fa-solid fa-comment-dots",
    title: "Watch with the translator on",
    body: "Every play gets one plain sentence about what just happened. Tap it for the longer version.",
  },
  {
    icon: "fa-solid fa-arrow-trend-up",
    title: "Notice you're following it",
    body: "You never sat down to study. Your Football IQ went up anyway.",
  },
];

const FEATURES = [
  {
    icon: "fa-solid fa-comment-dots",
    title: "“What just happened?”",
    body: "A one-line, jargon-free translation of every play, written by people rather than generated. Tap any play for the fuller explanation.",
  },
  {
    icon: "fa-solid fa-list-check",
    title: "The situation, in a sentence",
    body: "“2nd & 10” is meaningless if nobody ever told you. So it always comes with the sentence version, plus a field view showing where the ball actually is.",
  },
  {
    icon: "fa-solid fa-people-group",
    title: "Watch together",
    body: "Create a room, share the code, and everyone sees the same play at the same moment, with a leaderboard nobody asked for.",
  },
  {
    icon: "fa-solid fa-bullseye",
    title: "Predictions, not bets",
    body: "Four light questions before kickoff: who wins, who scores first, will anyone drop it. No money, no odds, no stake. Only smugness.",
  },
  {
    icon: "fa-solid fa-book-open",
    title: "Look anything up",
    body: `${TOTAL_CONCEPTS} short explainers, searchable mid-game, ordered from “what is a down” upwards. Read one or read all of them.`,
  },
  {
    icon: "fa-solid fa-circle-question",
    title: "The odd quick question",
    body: "A multiple-choice question at the moment it's relevant, never a quiz you have to sit. Get it right and the points are yours.",
  },
];

const NOT_THIS = [
  "No betting and no odds",
  "No fantasy leagues",
  "No ads, no subscription",
  "No chat to keep up with",
  "No stats you'd need a degree for",
  "No AI writing the explanations",
];

/**
 * The FAQ is also the source for the FAQPage structured data below, so the
 * answers a search engine sees are word-for-word the answers on the page.
 */
const FAQ = [
  {
    question: "Do I need to know anything about football?",
    answer:
      "No. That's the entire point. It assumes you know nothing and never makes you feel bad about it. The name is a promise, not a joke.",
  },
  {
    question: "Do I need an account?",
    answer:
      "No. Tap “Start watching” and you're in, with a nickname we pick for you. Signing in with Google is optional and only exists so your progress follows you to another device.",
  },
  {
    question: "Is this betting or fantasy football?",
    answer:
      "Neither. There is nothing to wager, no odds, no stake and no money anywhere in it. Predictions are four light questions asked before kickoff so you have something to be smug about afterwards.",
  },
  {
    question: "Does it cost anything?",
    answer:
      "No. It's free, with no ads and no subscription.",
  },
  {
    question: "Can I watch with other people?",
    answer:
      "Yes. Create a game room, share the code, and everyone in it follows the same game at the same point, whether they're on your sofa or in another city.",
  },
  {
    question: "Is the game data real NFL data?",
    answer:
      "Not yet. Today it runs on a scripted practice game between two real teams, so you can try the whole product before a real kickoff. Live game data is the next thing we connect.",
  },
];

const faqJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${absoluteUrl("/")}#faq`,
  mainEntity: FAQ.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});

/* ------------------------------------------------------------------ page */

/**
 * The marketing landing page: what `/` serves to anyone without a session.
 *
 * It is the only page in the product meant to be found by a search engine
 * (see `src/common/seo.js`), so it has to answer "what is this?" without a
 * session, without data and without an account. Every claim on it matches
 * what is actually built: the counts come from the content modules, and
 * the simulated-data caveat is stated on the page rather than buried.
 *
 * Signing in happens on `/welcome`, which owns every auth path. This page
 * only ever links to it.
 */
const Landing = ({ onBrowseIntent }) => {
  const navigate = useNavigate();
  const { startBrowsing } = useBrowseMode();

  useEffect(() => {
    applyJsonLd("faq", faqJsonLd());
    return () => removeJsonLd("faq");
  }, []);

  return (
    <div className={styles.Landing}>
      {/* ------------------------------------------------------- hero */}
      <section className={styles.hero} aria-labelledby="landing-title">
        <div className="container">
          <div className={styles.heroGrid}>
            <div className={styles.heroCopy}>
              {/* The shell's header already carries the wordmark, so the
                  headline gets to be the first thing read. */}
              <h1 id="landing-title" className={styles.title}>
                Football explained for people who don&rsquo;t know football.
              </h1>

              <p className={styles.lede}>
                You&rsquo;re on the sofa. Everyone else is shouting about 4th
                down. {SITE_NAME} follows the game with you and says what just
                happened, in words that assume you know nothing.
              </p>

              <div className={styles.ctaRow}>
                <Link
                  to={ROUTES.WELCOME}
                  className={`btn btn-primary btn-lg ${styles.cta}`}
                >
                  Start watching for free
                </Link>
                <a
                  href="#how-it-works"
                  className={`btn btn-outline-secondary btn-lg ${styles.cta}`}
                >
                  See how it works
                </a>
              </div>

              {/* The third door, for someone who wants to see the thing
                  before agreeing to anything at all. It grants read of the
                  football content and nothing else, which is why it is a
                  quiet text link rather than a third button competing with
                  the two above. */}
              <p className={styles.browseRow}>
                <button
                  type="button"
                  className={styles.browseLink}
                  onFocus={onBrowseIntent}
                  onMouseEnter={onBrowseIntent}
                  onClick={() => {
                    startBrowsing();
                    navigate(ROUTES.GAMES);
                  }}
                >
                  Or just have a look around first
                </button>
              </p>

              <ul className={styles.trustRow}>
                <li>
                  <i className="fa-solid fa-check" aria-hidden="true" /> No
                  account needed
                </li>
                <li>
                  <i className="fa-solid fa-check" aria-hidden="true" /> No
                  betting, ever
                </li>
                <li>
                  <i className="fa-solid fa-check" aria-hidden="true" /> Nothing
                  to install
                </li>
              </ul>
            </div>

            {/* A still life of the thing itself: the notation, then the
                sentence version, then the explanation of the last play.
                Kept readable by a screen reader: it is the clearest
                statement of what the product does anywhere on the page. */}
            <figure className={styles.preview}>
              <figcaption className="visually-hidden">
                An example of what you see while a game is on.
              </figcaption>

              <div
                className={`${styles.previewCard} app-team-wash`}
                style={teamWashStyle("BUF", "CAR")}
              >
                <div className={styles.previewTeams}>
                  <TeamLogo teamId="BUF" size="sm" />
                  <p className={styles.previewEyebrow}>Buffalo has the ball</p>
                  <TeamLogo teamId="CAR" size="sm" className={styles.previewAway} />
                </div>
                <p className={styles.previewDown}>2nd &amp; 10</p>
                <p className={styles.previewPlain}>
                  Buffalo needs 10 yards for a first down, a fresh set of four
                  tries. They have three tries left.
                </p>

                <div className={styles.previewField} aria-hidden="true">
                  <span className={styles.previewEndzone} />
                  <span className={styles.previewLine} />
                  <span className={styles.previewFirstDown} />
                  <span className={styles.previewBall} />
                  <span
                    className={`${styles.previewEndzone} ${styles.previewEndzoneRight}`}
                  />
                </div>
              </div>

              <div className={styles.previewExplain}>
                <p className={styles.previewExplainLabel}>
                  <i className="fa-solid fa-comment-dots" aria-hidden="true" />{" "}
                  What just happened?
                </p>
                <p className={styles.previewExplainBody}>
                  The pass sailed over the receiver&rsquo;s head. Nobody caught
                  it, so nothing moves and they try again.
                </p>
              </div>
            </figure>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------- the problem */}
      <section className={styles.section} aria-labelledby="landing-pain">
        <div className="container">
          <div className={styles.sectionHead}>
            <h2 id="landing-pain" className={styles.sectionTitle}>
              Sound familiar?
            </h2>
          </div>

          <ul className={styles.painList}>
            {PAINS.map((pain) => (
              <li key={pain} className={styles.pain}>
                {pain}
              </li>
            ))}
          </ul>
          <p className={styles.painAfter}>
            None of that is a you problem. Football is genuinely badly
            explained.
          </p>
        </div>
      </section>

      {/* ---------------------------------------------------- how it works */}
      <section
        id="how-it-works"
        className={`${styles.section} ${styles.sectionAlt}`}
        aria-labelledby="landing-how"
      >
        <div className="container">
          <div className={styles.sectionHead}>
            <p className="app-eyebrow">How it works</p>
            <h2 id="landing-how" className={styles.sectionTitle}>
              Three taps and a game on the telly.
            </h2>
          </div>

          <ol className={styles.steps}>
            {STEPS.map((step, i) => (
              <li key={step.title} className={styles.step}>
                <span className={styles.stepNumber}>{i + 1}</span>
                <span className={styles.stepIcon}>
                  <i className={step.icon} aria-hidden="true" />
                </span>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepBody}>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ------------------------------------------------------- features */}
      <section className={styles.section} aria-labelledby="landing-features">
        <div className="container">
          <div className={styles.sectionHead}>
            <p className="app-eyebrow">What you get</p>
            <h2 id="landing-features" className={styles.sectionTitle}>
              A companion, not a rulebook.
            </h2>
          </div>

          <ul className={styles.featureGrid}>
            {FEATURES.map((feature) => (
              <li key={feature.title} className={styles.feature}>
                <span className={styles.featureIcon}>
                  <i className={feature.icon} aria-hidden="true" />
                </span>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureBody}>{feature.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ------------------------------------------------------- the league */}
      <section
        className={`${styles.section} ${styles.sectionAlt}`}
        aria-labelledby="landing-clubs"
      >
        <div className="container">
          <div className={styles.sectionHead}>
            <p className="app-eyebrow">All 32 clubs</p>
            <h2 id="landing-clubs" className={styles.sectionTitle}>
              Whoever you end up watching.
            </h2>
            <p className={styles.sectionLede}>
              Every club, explained the same way. You don&rsquo;t need to know
              which is which yet, and by the end of a game you&rsquo;ll know
              two of them.
            </p>
          </div>

          <ul className={styles.clubs}>
            {BRANDED_TEAM_IDS.map((teamId) => (
              <li
                key={teamId}
                className={styles.club}
                style={singleTeamWashStyle(teamId)}
                title={teamFullName(teamId)}
              >
                <TeamLogo teamId={teamId} size="md" />
                <span className={styles.clubAbbrev}>{teamId}</span>
              </li>
            ))}
          </ul>

          <p className={styles.clubsAfter}>
            Club logos belong to their clubs. We&rsquo;re not affiliated with
            any of them, or with the NFL.
          </p>
        </div>
      </section>

      {/* ----------------------------------------------------- football IQ */}
      <section className={styles.section} aria-labelledby="landing-iq">
        <div className="container">
          <div className={styles.sectionHead}>
            <p className="app-eyebrow">Football IQ</p>
            <h2 id="landing-iq" className={styles.sectionTitle}>
              Six levels, from &ldquo;no idea&rdquo; to &ldquo;stop
              pretending&rdquo;.
            </h2>
            <p className={styles.sectionLede}>
              Points for watching, for reading an explainer, for getting a
              quick question right. It&rsquo;s a game mechanic, not a
              measurement of you.
            </p>
          </div>

          <ol className={styles.ladder}>
            {IQ_LEVELS.map((level) => (
              <li key={level.id} className={styles.ladderStep}>
                <span className={styles.ladderIcon}>
                  <i className={level.icon} aria-hidden="true" />
                </span>
                <span className={styles.ladderLabel}>{level.label}</span>
                <span className={`${styles.ladderRange} app-numeric`}>
                  {level.max === Infinity
                    ? `${level.min}+ IQ`
                    : `${level.min}–${level.max}`}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* -------------------------------------------------- what it is not */}
      <section
        className={`${styles.section} ${styles.sectionAlt}`}
        aria-labelledby="landing-not"
      >
        <div className="container">
          <div className={styles.notCard}>
            <div className={styles.sectionHead}>
              <h2 id="landing-not" className={styles.sectionTitle}>
                What it deliberately isn&rsquo;t.
              </h2>
              <p className={styles.sectionLede}>
                If it doesn&rsquo;t help you understand or enjoy the game, it
                isn&rsquo;t in here.
              </p>
            </div>

            <ul className={styles.notList}>
              {NOT_THIS.map((item) => (
                <li key={item} className={styles.notItem}>
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------ honest bit */}
      <section className={styles.section} aria-labelledby="landing-status">
        <div className="container">
          <div className={styles.statusCard} role="note">
            <StatusBadge tone="warning" icon="fa-solid fa-flask">
              Where it&rsquo;s at right now
            </StatusBadge>
            <h2 id="landing-status" className={styles.statusTitle}>
              Everything works. The football isn&rsquo;t live yet.
            </h2>
            <p className={styles.statusBody}>
              The whole product runs end to end today (explanations, rooms,
              predictions, Football IQ) on a <strong>scripted practice
              game</strong> between two real teams rather than a live NFL feed.
              Real teams, invented game. Nothing you see is a real NFL result,
              and it says so on every screen. Connecting live game data is the
              next thing on the list.
            </p>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------------- FAQ */}
      <section
        className={`${styles.section} ${styles.sectionAlt}`}
        aria-labelledby="landing-faq"
      >
        <div className="container">
          <div className={styles.sectionHead}>
            <h2 id="landing-faq" className={styles.sectionTitle}>
              Reasonable questions.
            </h2>
          </div>

          <dl className={styles.faq}>
            {FAQ.map(({ question, answer }) => (
              <div key={question} className={styles.faqItem}>
                <dt className={styles.faqQuestion}>{question}</dt>
                <dd className={styles.faqAnswer}>{answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* --------------------------------------------------------- last CTA */}
      <section className={styles.finalCta} aria-labelledby="landing-final">
        <div className="container">
          <h2 id="landing-final" className={styles.finalTitle}>
            Ready to have no idea, briefly?
          </h2>
          <p className={styles.finalBody}>{DEFAULT_DESCRIPTION}</p>
          <Link
            to={ROUTES.WELCOME}
            className={`btn btn-primary btn-lg ${styles.cta}`}
          >
            Start watching
          </Link>
          <p className={styles.finalSmall}>
            Free. No account. We&rsquo;ll give you a nickname.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Landing;
